/* eslint-disable react-refresh/only-export-components --
   Entry file. HMR for the entry boundary doesn't apply — the whole app
   reloads anyway when this file changes. */
// frontend/src/main.jsx
// Entry point with data router (createBrowserRouter) for React Router v6.4+ features
// including useBlocker (unsaved changes guard).

import { StrictMode, useState, useEffect, memo } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './styles/index.css'
import { createAppTheme } from './theme.js'
import { appRoutes } from './App.jsx'
import { queryClient } from './queries/client'

const router = createBrowserRouter(appRoutes);

/**
 * Isolated from AppRoot's theme state so that toggling dark/light mode
 * does NOT re-render the auth tree or any page component.
 *
 * MUI components still update because they subscribe to ThemeContext directly
 * via useTheme(). AG Grid updates via CSS custom properties.
 *
 * AuthProvider is inside the router (as a root layout route in App.jsx)
 * because it uses useNavigate().
 */
const AppShell = memo(function AppShell() {
  return <RouterProvider router={router} />;
});

function AppRoot() {
  const [theme, setTheme] = useState(createAppTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(createAppTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {/* QueryClientProvider must be ABOVE the router so any consumer
          (page, hook, context inside the route tree) can call useQuery /
          useMutation. The queryClient itself is a module-level singleton
          (queries/client.js), so imperative calls like queryClient.clear()
          from AuthContext.logout work without depending on this provider. */}
      <QueryClientProvider client={queryClient}>
        <AppShell />
        {/* Devtools mounted in dev only — adds ~80KB to dev bundle, zero
            cost in prod. Floating button bottom-right toggles the panel. */}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}
      </QueryClientProvider>
    </ThemeProvider>
  );
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <AppRoot />
  </StrictMode>,
)
